import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Clock, ShieldCheck } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { PassOnGate } from '../components/gsoc/PassOnGate';
import { EntryHelp } from '../components/gsoc/EntryHelp';

const TZ = 'America/Chicago';

/** Central-time value for a datetime-local input, optionally N hours back. */
const localNowInput = (hoursBack = 0) => {
  const d = new Date(Date.now() - hoursBack * 3600000);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d).reduce((a, p) => ({ ...a, [p.type]: p.value }), {});
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour === '24' ? '00' : parts.hour}:${parts.minute}`;
};

const fmtStamp = (iso) =>
  iso
    ? new Date(iso).toLocaleString('en-US', {
        timeZone: TZ, weekday: 'short', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit', second: '2-digit',
      })
    : '';

/** Native select styled to the platform's input look. Kept deliberately plain:
 *  this screen sits on a command-center console and is used at 3 a.m. */
const Select = ({ label, value, onChange, disabled, children, testId, error }) => (
  <div className="min-w-0">
    <Label className="text-sm font-semibold">{label}</Label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      data-testid={testId}
      className={`mt-1 w-full h-12 px-3 text-base rounded-md border bg-surface text-fg
        disabled:bg-disabled disabled:text-disabled-fg disabled:cursor-not-allowed
        ${error ? 'border-danger' : 'border-line'}`}
    >
      {children}
    </select>
    {error && <p className="text-xs text-danger mt-1" data-testid={`${testId}-error`}>{error}</p>}
  </div>
);

function EntryConsole() {
  const [params] = useSearchParams();
  const correctsId = params.get('corrects') || null;

  const [options, setOptions] = useState(null);
  const [sites, setSites] = useState([]);
  const [draft, setDraft] = useState(null);
  const [form, setForm] = useState({ client_id: '', site_id: '', activity_type_id: '', canned_comment_id: '', comments: '', occurred_at: '', work_order_number: '', account_number: '', correction_reason: '' });
  const [delayed, setDelayed] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [corrects, setCorrects] = useState(null);
  const [lastLogged, setLastLogged] = useState(null);
  const commentsRef = useRef(null);

  const startDraft = useCallback(async () => {
    const { data } = await api.post('/gsoc/entries/start');
    setDraft(data);
    setElapsed(0);
    setForm((f) => ({ ...f, activity_type_id: '', canned_comment_id: '', comments: '', occurred_at: '', work_order_number: '', account_number: '' }));
    setErrors({});
  }, []);

  useEffect(() => {
    (async () => {
      const { data: opts } = await api.get('/gsoc/form-options');
      setOptions(opts);
      setForm((f) => ({ ...f, client_id: opts.last_client_id || '' }));
      await startDraft();
      if (correctsId) {
        try {
          const { data } = await api.get(`/gsoc/entries/${correctsId}`);
          setCorrects(data);
          setForm((f) => ({ ...f, client_id: data.client_id, site_id: data.site_id, activity_type_id: data.activity_type_id }));
        } catch { toast.error('Could not load the entry being corrected'); }
      }
    })().catch(() => toast.error('Could not load the entry form'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sites follow the selected client, always.
  useEffect(() => {
    if (!form.client_id) { setSites([]); return; }
    api.get('/gsoc/sites', { params: { client_id: form.client_id } })
      .then(({ data }) => {
        const active = data.filter((s) => s.is_active);
        setSites(active);
        setForm((f) => ({ ...f, site_id: active.length === 1 ? active[0].id : (active.some((s) => s.id === f.site_id) ? f.site_id : '') }));
      })
      .catch(() => setSites([]));
  }, [form.client_id]);

  useEffect(() => {
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [draft?.id]);

  const types = useMemo(
    () => (options?.activity_types || []).filter((t) => !t.client_id || t.client_id === form.client_id),
    [options, form.client_id]
  );
  const canned = useMemo(
    () => (options?.canned_comments || []).filter(
      (c) => (!c.client_id || c.client_id === form.client_id) &&
             (!c.activity_type_id || c.activity_type_id === form.activity_type_id)),
    [options, form.client_id, form.activity_type_id]
  );

  const validate = () => {
    const e = {};
    if (!form.client_id) e.client_id = 'Select a client';
    if (!form.site_id) e.site_id = 'Select a site';
    if (!form.activity_type_id) e.activity_type_id = 'Select an activity type';
    if ((form.comments || '').trim().length < 3) e.comments = 'Enter at least 3 characters';
    if (correctsId && (form.correction_reason || '').trim().length < 3) e.correction_reason = 'A reason for the correction is required';
    if (delayed) {
      const limit = options?.backdate_limit_hours;
      if (!form.occurred_at) e.occurred_at = 'Set when it happened, or switch off "Delayed entry"';
      else if (form.occurred_at > localNowInput()) e.occurred_at = 'A time in the future is not allowed';
      else if (limit && form.occurred_at < localNowInput(limit)) {
        e.occurred_at = `Operators can go back ${limit} hours. Ask a supervisor to log anything older.`;
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (saving || !validate()) return;
    setSaving(true);
    try {
      const { data } = await api.post('/gsoc/entries', {
        draft_id: draft.id,
        client_id: form.client_id,
        site_id: form.site_id,
        activity_type_id: form.activity_type_id,
        comments: form.comments.trim(),
        canned_comment_id: form.canned_comment_id || null,
        corrects_entry_id: correctsId,
        correction_reason: correctsId ? form.correction_reason.trim() : null,
        occurred_at: delayed && form.occurred_at ? form.occurred_at : null,
        work_order_number: form.work_order_number.trim() || null,
        account_number: form.account_number.trim() || null,
      });
      toast.success(`Entry #${data.entry_number} logged`, { duration: 3000 });
      setLastLogged({ entry_number: data.entry_number, submitted_at: data.submitted_at });
      setCorrects(null);
      setDelayed(false);
      await startDraft();
      commentsRef.current?.focus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the entry');
      if (err.response?.status === 400) await startDraft();
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') submit(); };

  if (!options) return <div className="text-sm text-fg-muted" data-testid="gsoc-entry-loading">Loading...</div>;

  return (
    <div className="space-y-4" onKeyDown={onKeyDown} data-testid="gsoc-entry-page">
      {/* Console status bar — everything the operator needs to trust the stamp. */}
      <div className="rounded-lg border border-line bg-surface px-5 py-4 flex flex-wrap items-center gap-x-8 gap-y-3"
           data-testid="gsoc-entry-header">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-fg-muted">Entry started (Central)</p>
          <p className="text-xl lg:text-2xl font-bold text-fg tabular-nums whitespace-nowrap" data-testid="gsoc-started-at">
            {fmtStamp(draft?.started_at)}
          </p>
        </div>
        <div className="hidden lg:block w-px h-10 bg-[hsl(var(--line))]" aria-hidden="true" />
        <div>
          <p className="text-xs uppercase tracking-wide text-fg-muted">Operator</p>
          <p className="text-base font-semibold text-fg flex items-center gap-1">
            <ShieldCheck className="w-4 h-4" aria-hidden="true" />{options.operator_name}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-fg-muted">Shift</p>
          <p className="text-base font-semibold text-fg">{options.current_shift}</p>
        </div>
        <div className="ml-auto text-right">
          <p className="text-xs text-fg-muted flex items-center gap-1 justify-end">
            <Clock className="w-3 h-3" aria-hidden="true" />
            Server-stamped when you opened this form
            {elapsed > 30 && <span data-testid="gsoc-elapsed"> · open {Math.floor(elapsed / 60)}m {elapsed % 60}s</span>}
          </p>
          {lastLogged && (
            <p className="text-xs text-success mt-1" data-testid="gsoc-last-logged">
              Last logged: #{lastLogged.entry_number} at {fmtStamp(lastLogged.submitted_at)}
            </p>
          )}
        </div>
        <EntryHelp />
      </div>

      {corrects && (
        <div className="rounded-md border border-warning-border bg-warning-bg px-4 py-3 text-sm space-y-2" data-testid="gsoc-correction-banner">
          <div>
            <strong>Filing a correction</strong> for entry #{corrects.entry_number} logged {fmtStamp(corrects.submitted_at)}.
            Both entries stay in the log — nothing is overwritten.
          </div>
          <div>
            <Label htmlFor="gsoc-correction-reason" className="text-sm font-semibold">Reason for correction</Label>
            <Textarea
              id="gsoc-correction-reason"
              value={form.correction_reason}
              onChange={(e) => setForm((f) => ({ ...f, correction_reason: e.target.value }))}
              placeholder="Why is this correction being filed? (shown on the client report)"
              className={`mt-1 text-sm h-20 resize-none bg-surface ${errors.correction_reason ? 'border-danger' : ''}`}
              data-testid="gsoc-correction-reason-input"
            />
            {errors.correction_reason && <p className="text-xs text-danger mt-1" data-testid="gsoc-correction-reason-error">{errors.correction_reason}</p>}
          </div>
        </div>
      )}

      {/* Wide console form: one row of selectors, then the event details beside the
          submit action so nothing important sits below the fold on a 1080p screen. */}
      <div className="rounded-lg border border-line bg-surface p-5 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <Select label="Client" value={form.client_id} error={errors.client_id} testId="gsoc-client-select"
                  onChange={(v) => setForm((f) => ({ ...f, client_id: v }))}>
            <option value="">Select a client...</option>
            {options.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>

          <Select label="Site" value={form.site_id} error={errors.site_id} testId="gsoc-site-select"
                  disabled={!form.client_id} onChange={(v) => setForm((f) => ({ ...f, site_id: v }))}>
            <option value="">{form.client_id ? 'Select a site...' : 'Choose a client first'}</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>

          <Select label="Activity Type" value={form.activity_type_id} error={errors.activity_type_id} testId="gsoc-type-select"
                  onChange={(v) => setForm((f) => ({ ...f, activity_type_id: v }))}>
            <option value="">Select an activity type...</option>
            {types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </Select>

          <Select label="Quick Comment (optional)" value={form.canned_comment_id} testId="gsoc-canned-select"
                  onChange={(v) => {
                    const picked = canned.find((c) => c.id === v);
                    setForm((f) => ({ ...f, canned_comment_id: v, comments: picked ? picked.text : f.comments }));
                  }}>
            <option value="">None</option>
            {canned.map((c) => <option key={c.id} value={c.id}>{c.text}</option>)}
          </Select>
        </div>

        <div className="flex flex-col lg:flex-row gap-4 lg:items-stretch">
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <Label htmlFor="gsoc-comments" className="text-sm font-semibold">Operator Comments / Event Details</Label>
              <label className="flex items-center gap-2 text-sm text-fg-muted cursor-pointer">
                <input type="checkbox" checked={delayed} data-testid="gsoc-delayed-toggle"
                       className="w-4 h-4 accent-[hsl(var(--primary))]"
                       onChange={(e) => {
                         setDelayed(e.target.checked);
                         setForm((f) => ({ ...f, occurred_at: e.target.checked ? localNowInput() : '' }));
                       }} />
                Delayed entry
              </label>
            </div>
            <Textarea
              id="gsoc-comments"
              ref={commentsRef}
              rows={4}
              value={form.comments}
              onChange={(e) => setForm((f) => ({ ...f, comments: e.target.value }))}
              placeholder="What happened, what you saw, and what you did."
              className={`mt-1 text-base h-[132px] resize-none ${errors.comments ? 'border-danger' : ''}`}
              data-testid="gsoc-comments-input"
            />
            {errors.comments && <p className="text-xs text-danger mt-1" data-testid="gsoc-comments-error">{errors.comments}</p>}
            {delayed && (
              <div className="mt-2 flex flex-wrap items-center gap-3 rounded-md border border-info-border bg-info-bg px-3 py-2">
                <Label className="text-sm font-semibold whitespace-nowrap">Occurred at (Central)</Label>
                <input
                  type="datetime-local"
                  value={form.occurred_at}
                  max={localNowInput()}
                  min={options.backdate_limit_hours ? localNowInput(options.backdate_limit_hours) : undefined}
                  onChange={(e) => setForm((f) => ({ ...f, occurred_at: e.target.value }))}
                  data-testid="gsoc-occurred-at-input"
                  className={`h-10 px-3 text-base rounded-md border bg-surface text-fg ${errors.occurred_at ? 'border-danger' : 'border-line'}`}
                />
                <p className="text-xs text-fg-secondary flex-1 min-w-[220px]">
                  The log still records the time you submit this, and the entry is marked as logged late.
                  {options.backdate_limit_hours
                    ? ` Up to ${options.backdate_limit_hours} hours back.`
                    : ' No limit on your account.'}
                </p>
              </div>
            )}
            {errors.occurred_at && <p className="text-xs text-danger mt-1" data-testid="gsoc-occurred-error">{errors.occurred_at}</p>}
          </div>

          <div className="lg:w-64 flex flex-col justify-end gap-2">
            <div className="grid grid-cols-2 lg:grid-cols-1 gap-2">
              <div>
                <Label htmlFor="gsoc-work-order" className="text-xs text-fg-muted">Work Order # (optional)</Label>
                <Input id="gsoc-work-order" value={form.work_order_number} placeholder="WO-10482"
                       className="mt-1 h-9" data-testid="gsoc-work-order-input"
                       onChange={(e) => setForm((f) => ({ ...f, work_order_number: e.target.value }))} />
              </div>
              <div>
                <Label htmlFor="gsoc-account" className="text-xs text-fg-muted">Account # (optional)</Label>
                <Input id="gsoc-account" value={form.account_number} placeholder="ACCT-5567"
                       className="mt-1 h-9" data-testid="gsoc-account-input"
                       onChange={(e) => setForm((f) => ({ ...f, account_number: e.target.value }))} />
              </div>
            </div>
            <Button onClick={submit} disabled={saving} className="w-full h-16 text-base" data-testid="gsoc-submit-btn">
              {saving ? 'Saving...' : 'Submit Entry'}
            </Button>
            <p className="text-xs text-fg-muted text-center leading-snug">
              Entries are permanent. <strong>Ctrl+Enter</strong> submits.
              To fix a mistake, file a correction from the log.
              <br />Work order and account numbers stay internal — they are not on the client report.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function GSOCEntry() {
  return <PassOnGate><EntryConsole /></PassOnGate>;
}
